import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  type DocumentData,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Region {
  id: string;
  name: string;
  isDeleted: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  deletedAt?: Timestamp;
}

export interface CreateRegionData {
  name: string;
}

export interface RegionServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToRegion(id: string, data: DocumentData): Region {
  return {
    id,
    name: data.name as string,
    isDeleted: data.isDeleted as boolean,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
    deletedAt: data.deletedAt as Timestamp | undefined,
  };
}

function toServiceError(error: unknown): RegionServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

/** Returns true if a non-deleted region with the given name already exists in the org. */
async function nameExists(orgId: string, name: string, excludeId?: string): Promise<boolean> {
  const q = query(
    collection(db, 'orgs', orgId, 'regions'),
    where('isDeleted', '==', false),
    where('name', '==', name.trim()),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.some(d => d.id !== excludeId);
}

// ─── Service functions ────────────────────────────────────────────────────────

/** Returns all non-deleted regions in an org, alphabetically. */
export async function getAllRegions(orgId: string): Promise<Region[]> {
  try {
    const q = query(
      collection(db, 'orgs', orgId, 'regions'),
      where('isDeleted', '==', false),
      orderBy('name', 'asc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => docToRegion(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Returns a single region by document ID, or null if not found. */
export async function getRegionById(orgId: string, id: string): Promise<Region | null> {
  try {
    const snapshot = await getDoc(doc(db, 'orgs', orgId, 'regions', id));
    if (!snapshot.exists()) return null;
    return docToRegion(snapshot.id, snapshot.data());
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Creates a new region in an org. Throws if the name already exists.
 */
export async function createRegion(orgId: string, data: CreateRegionData): Promise<Region> {
  try {
    const trimmedName = data.name.trim();
    if (await nameExists(orgId, trimmedName)) {
      throw { code: 'already-exists', message: `A region named "${trimmedName}" already exists.` };
    }
    const ref = doc(collection(db, 'orgs', orgId, 'regions'));
    await setDoc(ref, {
      name: trimmedName,
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const snapshot = await getDoc(ref);
    return docToRegion(snapshot.id, snapshot.data()!);
  } catch (error) {
    if ((error as RegionServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/** Updates the name of an existing region. Throws if the new name already exists. */
export async function updateRegion(orgId: string, id: string, name: string): Promise<void> {
  try {
    const trimmedName = name.trim();
    if (await nameExists(orgId, trimmedName, id)) {
      throw { code: 'already-exists', message: `A region named "${trimmedName}" already exists.` };
    }
    await updateDoc(doc(db, 'orgs', orgId, 'regions', id), {
      name: trimmedName,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    if ((error as RegionServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/** Soft-deletes a region (sets isDeleted + deletedAt). */
export async function deleteRegion(orgId: string, id: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'orgs', orgId, 'regions', id), {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Subscribes to real-time updates for all active regions in an org.
 * Returns an unsubscribe function to clean up the listener.
 */
export function subscribeToRegions(
  orgId: string,
  onData: (_regions: Region[]) => void,
  onError: (_error: RegionServiceError) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'orgs', orgId, 'regions'),
    where('isDeleted', '==', false),
    orderBy('name', 'asc'),
  );
  return onSnapshot(
    q,
    snapshot => onData(snapshot.docs.map(d => docToRegion(d.id, d.data()))),
    err => onError(toServiceError(err)),
  );
}
